/**
 * Wizard chrome for the Display, Performance Max and Shopping workspaces — the
 * same shape App Promotion, YouTube and Search use: step rail · goal strip ·
 * sections · sticky live preview · review facts · resumable build log.
 *
 * Everything here is MODULE SCOPE on purpose: components defined inside a
 * page remount on every keystroke and lose focus.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  Check, CheckCircle2, ChevronDown, ChevronRight, CircleAlert, Copy, Film, Globe, Lock, Plus, Trash2, X, XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { EmptyState } from '@/components/EmptyState';
import { ErrorBox, Label, Section } from '@/components/google-ads/youtube/youtubeShared';
import { parseYoutubeId } from '@/pages/google-ads/googleAdsTargeting';
import { MoneyInput, Pub, StepIcon, today, toMicros } from './objectiveShared';

// ── page states ──────────────────────────────────────────────────────────

export const PageSpinner = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

export const ConnectFirst = ({ what = 'campaigns' }: { what?: string }) => (
  <EmptyState
    icon={<Globe size={36} className="text-text-muted" />}
    title="Connect a Google Ads account first"
    description={`Authorise Google and choose a customer account, then come back to build ${what}.`}
    action={<Link to="/google-ads/account"><Button iconLeft={<Globe size={14} />}>Connect Google Ads</Button></Link>}
  />
);

// ── step rail ────────────────────────────────────────────────────────────

export interface RailStep<K extends string> { key: K; label: string; icon: LucideIcon }

/** Left rail (lg+). Steps behind the current one show a check, or a cross while they still have issues. */
export function WizardRail<K extends string>({ steps, step, onStep, issues, summary }: {
  steps: ReadonlyArray<RailStep<K>>; step: K; onStep: (k: K) => void; issues: (k: K) => number; summary?: React.ReactNode;
}) {
  const idx = steps.findIndex((s) => s.key === step);
  return (
    <div className="hidden w-52 shrink-0 lg:block">
      <Card className="sticky top-24 p-3">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const on = s.key === step;
          const n = issues(s.key);
          return (
            <button key={s.key} type="button" onClick={() => onStep(s.key)} aria-current={on ? 'step' : undefined}
              className={clsx('mb-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm transition-colors',
                on ? 'bg-primary-soft font-medium text-primary' : 'text-text-secondary hover:bg-surface-alt')}>
              {i < idx && n === 0 ? <CheckCircle2 size={16} className="shrink-0 text-success" />
                : i < idx && n > 0 ? <XCircle size={16} className="shrink-0 text-danger" />
                : <Icon size={16} className="shrink-0" />}
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
            </button>
          );
        })}
        {summary && <div className="mt-3 space-y-0.5 border-t border-border px-3 pt-3 text-2xs text-text-muted">{summary}</div>}
      </Card>
    </div>
  );
}

/** The same steps as pills below lg, where the rail is hidden. */
export function WizardStepPills<K extends string>({ steps, step, onStep }: {
  steps: ReadonlyArray<RailStep<K>>; step: K; onStep: (k: K) => void;
}) {
  return (
    <div className="flex gap-1 overflow-x-auto rounded-xl border border-border bg-surface p-1 lg:hidden">
      {steps.map((s, i) => (
        <button key={s.key} type="button" onClick={() => onStep(s.key)}
          className={clsx('shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium', s.key === step ? 'bg-primary-soft text-primary' : 'text-text-secondary')}>
          {i + 1}. {s.label}
        </button>
      ))}
    </div>
  );
}

/** "Goal: … · Type: … · Served via: …" strip above every step. */
export function GoalStrip({ items, children }: { items: Array<{ label: string; value: React.ReactNode }>; children?: React.ReactNode }) {
  return (
    <Card padded={false} className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2.5 text-xs text-text-muted">
      {items.map((it) => (
        <span key={it.label}>{it.label}: <span className="font-medium text-text-primary">{it.value}</span></span>
      ))}
      {children}
    </Card>
  );
}

/** Objective intro — what this campaign type is and what Google will build from it. */
export function IntroBanner({ icon: Icon, title, children, points }: {
  icon: LucideIcon; title: string; children: React.ReactNode; points?: string[];
}) {
  return (
    <div className="rounded-2xl border border-primary/30 bg-primary-soft/25 px-4 py-3.5">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white"><Icon size={18} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold text-text-primary">{title}</div>
          <div className="mt-0.5 text-xs leading-relaxed text-text-muted">{children}</div>
          {points && points.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {points.map((p) => (
                <span key={p} className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-surface/70 px-2.5 py-1 text-2xs font-medium text-text-secondary">
                  <Check size={11} className="text-success" />{p}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Neutral / warning callout inside a section. */
export function Callout({ tone = 'info', icon: Icon = CircleAlert, children, className }: {
  tone?: 'info' | 'warn' | 'ok' | 'muted'; icon?: LucideIcon; children: React.ReactNode; className?: string;
}) {
  const tones = {
    info: 'border-info/30 bg-info-soft/40 text-text-secondary [&_svg]:text-info',
    warn: 'border-warning/35 bg-warning-soft/60 text-text-secondary [&_svg]:text-warning',
    ok: 'border-success/30 bg-success-soft text-text-secondary [&_svg]:text-success',
    muted: 'border-border bg-surface-alt/60 text-text-muted',
  } as const;
  return (
    <div className={clsx('flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-2xs leading-relaxed', tones[tone], className)}>
      <Icon size={14} className="mt-px shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

// ── choices ──────────────────────────────────────────────────────────────

export interface ChoiceOption<T extends string> {
  key: T; title: string; desc: string; hint?: string; icon: LucideIcon;
  /** Google refuses these until the account tracks conversions (CONVERSION_TRACKING_NOT_ENABLED). */
  needsConversions?: boolean;
}

/** Icon cards for bidding and other one-of choices. Conversion strategies lock when Google says tracking is missing. */
export function ChoiceCards<T extends string>({ options, value, onChange, recommended, conversionBlocked, disabled, cols = 2 }: {
  options: ReadonlyArray<ChoiceOption<T>>; value: T; onChange: (v: T) => void; recommended?: T;
  conversionBlocked?: boolean; disabled?: boolean; cols?: 2 | 3;
}) {
  return (
    <div className={clsx('grid grid-cols-1 gap-2.5', cols === 3 ? 'md:grid-cols-3' : 'sm:grid-cols-2')}>
      {options.map((o) => {
        const locked = !!(o.needsConversions && conversionBlocked);
        const on = value === o.key;
        const Icon = o.icon;
        return (
          <button key={o.key} type="button" disabled={disabled || locked} onClick={() => onChange(o.key)} aria-pressed={on}
            className={clsx(
              'relative flex gap-3 rounded-xl border p-3.5 text-left transition-colors',
              on ? 'border-primary bg-primary-soft/40' : 'border-border bg-surface hover:bg-surface-alt',
              (disabled || locked) && 'cursor-not-allowed opacity-60 hover:bg-surface',
            )}>
            <span className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', on ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-muted')}>
              {locked ? <Lock size={15} /> : <Icon size={16} />}
            </span>
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-text-primary">{o.title}</span>
                {o.key === recommended && !locked && <span className="rounded-full bg-primary-soft px-1.5 py-0.5 text-[10px] font-semibold text-primary">Recommended</span>}
              </span>
              <span className="mt-0.5 block text-2xs leading-4 text-text-muted">{o.desc}</span>
              {(o.hint || locked) && (
                <span className={clsx('mt-1 block text-2xs font-medium', locked ? 'text-warning' : 'text-text-secondary')}>
                  {locked ? 'Needs conversion tracking on this account' : o.hint}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Radio / checkbox card — merchant accounts, store location groups, and similar. */
export function RadioCard({ on, onClick, title, desc, icon: Icon, disabled, multi, right }: {
  on: boolean; onClick: () => void; title: string; desc?: React.ReactNode; icon?: LucideIcon; disabled?: boolean; multi?: boolean; right?: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on}
      className={clsx('flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        on ? 'border-primary bg-primary-soft/40' : 'border-border hover:bg-surface-alt')}>
      <span className={clsx('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border', multi ? 'rounded' : 'rounded-full',
        on ? 'border-primary bg-primary text-on-primary' : 'border-border-strong')}>
        {on && (multi ? <Check size={11} /> : <span className="h-1.5 w-1.5 rounded-full bg-on-primary" />)}
      </span>
      {Icon && <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', on ? 'bg-primary/15 text-primary' : 'bg-surface-alt text-text-muted')}><Icon size={15} /></span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-text-primary">{title}</span>
        {desc && <span className="mt-0.5 block text-2xs leading-4 text-text-muted">{desc}</span>}
      </span>
      {right}
    </button>
  );
}

/** Small segmented control (preview formats, priority levels). */
export function Segmented<T extends string>({ options, value, onChange, size = 'sm', ariaLabel }: {
  options: ReadonlyArray<{ value: T; label: string; icon?: LucideIcon; title?: string }>; value: T; onChange: (v: T) => void;
  size?: 'xs' | 'sm'; ariaLabel?: string;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="inline-flex overflow-hidden rounded-lg border border-border bg-surface">
      {options.map((o) => {
        const Icon = o.icon;
        const on = o.value === value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} title={o.title ?? o.label} onClick={() => onChange(o.value)}
            className={clsx('inline-flex items-center gap-1 font-medium transition-colors',
              size === 'xs' ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-2xs',
              on ? 'bg-primary-soft text-primary' : 'text-text-secondary hover:bg-surface-alt')}>
            {Icon && <Icon size={size === 'xs' ? 11 : 12} />}{o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Conversion-tracking status above the bidding cards. */
export interface ConversionReadinessLike { enabledConversionActions: number | null; conversionBiddingAllowed: boolean | null }
export function ConversionNote({ readiness, blockedText }: { readiness: ConversionReadinessLike | null; blockedText?: string }) {
  if (!readiness) return null;
  const ok = readiness.conversionBiddingAllowed === true;
  const blocked = readiness.conversionBiddingAllowed === false;
  const n = readiness.enabledConversionActions;
  return (
    <div className={clsx('mb-3 flex items-start gap-2 rounded-lg px-3 py-2 text-2xs',
      ok ? 'bg-success-soft text-success' : blocked ? 'bg-warning-soft text-warning' : 'bg-surface-alt text-text-muted')}>
      {ok ? <CheckCircle2 size={13} className="mt-px shrink-0" /> : <CircleAlert size={13} className="mt-px shrink-0" />}
      <span>
        {ok
          ? `Conversion tracking is set up on this account${n ? ` (${n} conversion action${n === 1 ? '' : 's'})` : ''} — conversion strategies are available.`
          : blocked
            ? (blockedText ?? 'This account has no conversion tracking yet, so Google refuses conversion strategies. Set it up in Google Ads → Goals → Conversions to unlock them.')
            : 'Could not check conversion tracking — conversion strategies only work once it is set up.'}
      </span>
    </div>
  );
}

// ── budget + dates ───────────────────────────────────────────────────────

export function BudgetDates({ budget, onBudget, start, onStart, end, onEnd, currency, budgetHint }: {
  budget: string; onBudget: (v: string) => void; start: string; onStart: (v: string) => void; end: string; onEnd: (v: string) => void;
  currency?: string | null; budgetHint?: string;
}) {
  const monthly = toMicros(budget) > 0 ? Number(budget) * 30.4 : 0;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <Label>Daily budget{currency ? ` (${currency})` : ''}</Label>
        <div className="mt-1.5"><MoneyInput value={budget} onChange={onBudget} currency={currency} placeholder="e.g. 50" /></div>
        {budgetHint && <p className="mt-1 text-2xs text-text-muted">{budgetHint}</p>}
      </div>
      <div>
        <Label>Monthly estimate</Label>
        <div className="mt-1.5 flex h-10 items-center rounded-lg bg-surface-alt px-3.5 text-sm text-text-secondary">
          {monthly ? `≈ ${monthly.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency ?? ''}` : '—'}
        </div>
        <p className="mt-1 text-2xs text-text-muted">Google may spend up to 2× on a busy day and evens it out over the month.</p>
      </div>
      <div>
        <Label>Start date</Label>
        <Input className="mt-1.5" type="date" value={start} min={today()} onChange={(e) => onStart(e.target.value)} />
        <p className="mt-1 text-2xs text-text-muted">Blank or today = starts when you enable it.</p>
      </div>
      <div>
        <Label>End date <span className="font-normal text-text-muted">(optional)</span></Label>
        <Input className="mt-1.5" type="date" value={end} min={start || today()} onChange={(e) => onEnd(e.target.value)} />
      </div>
    </div>
  );
}

// ── draft cards ──────────────────────────────────────────────────────────

export const IconBtn = ({ title, onClick, disabled, danger, small, children }: {
  title: string; onClick: () => void; disabled?: boolean; danger?: boolean; small?: boolean; children: React.ReactNode;
}) => (
  <button type="button" title={title} aria-label={title} disabled={disabled} onClick={onClick}
    className={clsx('flex shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt disabled:opacity-40',
      small ? 'h-6 w-6' : 'h-7 w-7', danger ? 'hover:text-danger' : 'hover:text-text-primary')}>
    {children}
  </button>
);

/** Collapsible card for an ad group / asset group draft. */
export function DraftCard({ open, onToggle, state, icon, thumb, title, sub, tags, onDuplicate, onRemove, busy, children }: {
  open: boolean; onToggle: () => void; state?: Pub; icon: React.ReactNode; thumb?: React.ReactNode; title: string; sub?: string;
  tags?: React.ReactNode; onDuplicate?: () => void; onRemove?: () => void; busy?: boolean; children: React.ReactNode;
}) {
  return (
    <Card padded={false} className="overflow-hidden">
      <div className={clsx('flex items-center gap-2.5 px-4 py-3', open && 'border-b border-border')}>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left" onClick={onToggle} aria-expanded={open}>
          {state && state !== 'idle' ? <StepIcon s={state} />
            : open ? <ChevronDown size={15} className="shrink-0 text-text-muted" /> : <ChevronRight size={15} className="shrink-0 text-text-muted" />}
          {thumb ?? <span className="shrink-0 text-text-muted">{icon}</span>}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-text-primary">{title}</div>
            {sub && <div className="truncate text-2xs text-text-muted">{sub}</div>}
          </div>
        </button>
        {tags}
        {onDuplicate && <IconBtn title="Duplicate" onClick={onDuplicate} disabled={busy}><Copy size={14} /></IconBtn>}
        {onRemove && <IconBtn danger title="Remove" onClick={onRemove} disabled={busy}><Trash2 size={14} /></IconBtn>}
      </div>
      {open && <div className="space-y-4 p-4">{children}</div>}
    </Card>
  );
}

/** Titled block inside a draft card ("Audience", "Content", "Images"…). */
export function Panel({ title, hint, right, children, className }: {
  title: string; hint?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={clsx('rounded-xl border border-border p-3.5', className)}>
      <div className="mb-3 flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-text-primary">{title}</div>
          {hint && <p className="mt-0.5 text-2xs leading-4 text-text-muted">{hint}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

// ── text ─────────────────────────────────────────────────────────────────

const countTone = (len: number, max: number) => (len > max ? 'text-danger' : len > max - 4 ? 'text-warning' : 'text-text-muted/70');

/** One input with a live character counter against Google's limit. */
export function CountedInput({ value, onChange, maxLen, placeholder, disabled, iconLeft, ariaLabel, className }: {
  value: string; onChange: (v: string) => void; maxLen: number; placeholder?: string; disabled?: boolean;
  iconLeft?: React.ReactNode; ariaLabel?: string; className?: string;
}) {
  return (
    <div className={clsx('relative', className)}>
      <Input value={value} disabled={disabled} maxLength={maxLen + 20} placeholder={placeholder} aria-label={ariaLabel} iconLeft={iconLeft}
        onChange={(e) => onChange(e.target.value)} className="pr-14" invalid={value.length > maxLen} />
      <span className={clsx('pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-2xs tabular-nums', countTone(value.length, maxLen))}>
        {value.length}/{maxLen}
      </span>
    </div>
  );
}

/** 7+ digits with only spaces, dashes, dots or brackets between them — Google disapproves phone numbers in ad text. */
export const looksLikePhone = (s: string) => /(?:\+?\d[\s().-]{0,2}){7,}/.test(s);

/** Headlines / descriptions: one counted input per line. `firstMaxLen` covers PMAX's short first description. */
export function TextLines({ label, items, onChange, min, max, maxLen, firstMaxLen, placeholder, disabled, cols = 1, hint }: {
  label: string; items: string[]; onChange: (v: string[]) => void; min: number; max: number; maxLen: number; firstMaxLen?: number;
  placeholder?: string[]; disabled?: boolean; cols?: 1 | 2; hint?: string;
}) {
  const count = items.map((x) => x.trim()).filter(Boolean).length;
  const phoneLines = items.flatMap((v, i) => (looksLikePhone(v) ? [i + 1] : []));
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <Label>{label}</Label>
        <span className={clsx('text-2xs tabular-nums', count < min ? 'text-warning' : 'text-text-muted')}>{count}/{max}{count < min ? ` · need ${min}` : ''}</span>
      </div>
      {hint && <p className="mt-0.5 text-2xs text-text-muted">{hint}</p>}
      <div className={clsx('mt-1.5 grid gap-1.5', cols === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1')}>
        {items.map((v, i) => {
          const cap = i === 0 && firstMaxLen ? firstMaxLen : maxLen;
          return (
            <div key={i} className="flex items-center gap-1">
              <CountedInput className="min-w-0 flex-1" value={v} maxLen={cap} disabled={disabled} placeholder={placeholder?.[i] ?? ''} ariaLabel={`${label} ${i + 1}`}
                onChange={(next) => onChange(items.map((x, j) => (j === i ? next : x)))} />
              {items.length > min && !disabled && (
                <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}
                  className="flex h-8 w-7 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"><X size={13} /></button>
              )}
            </div>
          );
        })}
      </div>
      {phoneLines.length > 0 && (
        <p className="mt-1.5 text-2xs text-warning">
          {label.replace(/s$/, '')} {phoneLines.join(', ')} looks like a phone number — Google rejects phone numbers in ad text.
        </p>
      )}
      {items.length < max && !disabled && (
        <button type="button" onClick={() => onChange([...items, ''])} className="mt-1.5 inline-flex items-center gap-1 text-2xs font-medium text-primary hover:underline">
          <Plus size={11} />Add {label.toLowerCase().replace(/s$/, '')}
        </button>
      )}
    </div>
  );
}

/** Problems with a list of text assets, phrased for "needs …". */
export function textIssues(label: string, items: string[], min: number, maxLen: number, firstMaxLen?: number): string[] {
  const out: string[] = [];
  const filled = items.map((x) => x.trim()).filter(Boolean);
  if (filled.length < min) out.push(`${min - filled.length} more ${label.replace(/s$/, '')}${min - filled.length === 1 ? '' : 's'}`);
  if (new Set(filled.map((x) => x.toLowerCase())).size !== filled.length) out.push(`different ${label} (two are the same)`);
  if (items.some((x, i) => x.length > (i === 0 && firstMaxLen ? firstMaxLen : maxLen))) {
    out.push(firstMaxLen ? `${label} within limits (first ≤ ${firstMaxLen}, others ≤ ${maxLen})` : `${label} ≤ ${maxLen} characters`);
  }
  return out;
}

/**
 * Chip input — type and press Enter, or paste a comma / newline separated
 * list. Backspace on an empty field removes the last chip. Dedupes
 * case-insensitively.
 */
export function TagInput({ values, onChange, placeholder, disabled, max = 200, renderChip, problem }: {
  values: string[]; onChange: (v: string[]) => void; placeholder?: string; disabled?: boolean; max?: number;
  renderChip?: (v: string) => React.ReactNode;
  /** Why a value is refused (shown red on the chip), or null. */
  problem?: (v: string) => string | null;
}) {
  const [draft, setDraft] = useState('');
  const commit = (raw: string) => {
    const seen = new Set(values.map((v) => v.toLowerCase()));
    const next = [...values];
    for (const piece of raw.split(/\n|,/)) {
      const t = piece.trim();
      if (!t || seen.has(t.toLowerCase())) continue;
      seen.add(t.toLowerCase());
      next.push(t);
    }
    onChange(next.slice(0, max));
    setDraft('');
  };
  return (
    <div className={clsx('rounded-lg border border-border bg-surface px-2 py-1.5 transition-colors focus-within:border-primary focus-within:ring-4 focus-within:ring-primary-soft',
      disabled && 'cursor-not-allowed opacity-60')}>
      <div className="flex flex-wrap items-center gap-1.5">
        {values.map((v) => {
          const bad = problem?.(v);
          return (
            <span key={v} title={bad ?? undefined}
              className={clsx('inline-flex max-w-full items-center gap-1 rounded-md py-0.5 pl-2 pr-1 text-xs',
                bad ? 'bg-danger-soft text-danger' : 'bg-surface-alt text-text-primary')}>
              <span className="truncate">{renderChip ? renderChip(v) : v}</span>
              {!disabled && (
                <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`}
                  className="shrink-0 rounded p-0.5 text-text-muted hover:text-danger"><X size={11} /></button>
              )}
            </span>
          );
        })}
        <input
          value={draft} disabled={disabled} placeholder={values.length ? '' : placeholder}
          className="min-w-[160px] flex-1 bg-transparent py-1 text-sm text-text-primary outline-none placeholder:text-text-muted"
          onChange={(e) => { const v = e.target.value; if (/[\n,]/.test(v)) commit(v); else setDraft(v); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
            else if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
          }}
          onPaste={(e) => { const text = e.clipboardData.getData('text'); if (/[\n,]/.test(text)) { e.preventDefault(); commit(`${draft}${text}`); } }}
          onBlur={() => { if (draft.trim()) commit(draft); }}
        />
      </div>
    </div>
  );
}

// ── YouTube videos ───────────────────────────────────────────────────────

/** YouTube URLs with live thumbnails — the App Promotion pattern. */
export function YouTubeList({ items, onChange, max, disabled }: { items: string[]; onChange: (v: string[]) => void; max: number; disabled?: boolean }) {
  return (
    <div className="space-y-2">
      {items.map((v, i) => {
        const vid = parseYoutubeId(v);
        return (
          <div key={i} className="flex items-center gap-2">
            {vid
              ? <img src={`https://i.ytimg.com/vi/${vid}/default.jpg`} alt="" className="h-9 w-16 shrink-0 rounded object-cover" />
              : <span className="flex h-9 w-16 shrink-0 items-center justify-center rounded bg-surface-alt text-text-muted"><Film size={13} /></span>}
            <Input placeholder="https://www.youtube.com/watch?v=…" value={v} disabled={disabled} aria-label={`Video ${i + 1}`} invalid={!!v.trim() && !vid}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} />
            <button type="button" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger disabled:opacity-40"
              aria-label={`Remove video ${i + 1}`} disabled={disabled || (items.length <= 1 && !v)}
              onClick={() => onChange(items.length <= 1 ? [''] : items.filter((_, j) => j !== i))}>
              <Trash2 size={14} />
            </button>
          </div>
        );
      })}
      {items.length < max && !disabled && (
        <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />} onClick={() => onChange([...items, ''])}>Add video</Button>
      )}
    </div>
  );
}

export const validVideoIds = (items: string[]) => Array.from(new Set(items.map((v) => parseYoutubeId(v)).filter((v): v is string => !!v)));
export const badVideoLines = (items: string[]) => items.filter((v) => v.trim() && !parseYoutubeId(v)).length;

// ── strength ─────────────────────────────────────────────────────────────

export interface StrengthRow { label: string; have: number; min: number; target: number; weight: number }
export interface Strength { level: 0 | 1 | 2 | 3 | 4; label: 'Incomplete' | 'Poor' | 'Average' | 'Good' | 'Excellent'; tips: string[] }

/**
 * An estimate of Google's Ad strength from asset coverage: every required
 * minimum met, then the closer each asset type gets to Google's recommended
 * count, the higher the score. Google's own rating arrives after review.
 */
export function strengthOf(rows: StrengthRow[]): Strength {
  const missing = rows.filter((r) => r.have < r.min);
  if (missing.length) {
    return { level: 0, label: 'Incomplete', tips: missing.map((r) => `Add ${r.min - r.have} more ${r.label.toLowerCase()} — Google needs at least ${r.min}.`).slice(0, 3) };
  }
  const total = rows.reduce((n, r) => n + r.weight, 0) || 1;
  const score = rows.reduce((n, r) => n + r.weight * Math.min(r.have / Math.max(r.target, 1), 1), 0) / total;
  const tips = rows
    .filter((r) => r.have < r.target)
    .sort((a, b) => b.weight * (1 - b.have / b.target) - a.weight * (1 - a.have / a.target))
    .slice(0, 3)
    .map((r) => `Add ${r.target - r.have} more ${r.label.toLowerCase()} — Google recommends ${r.target}.`);
  const level = score < 0.45 ? 1 : score < 0.7 ? 2 : score < 0.9 ? 3 : 4;
  return { level, label: (['Incomplete', 'Poor', 'Average', 'Good', 'Excellent'] as const)[level], tips };
}

const STRENGTH_TONE: Record<Strength['level'], string> = {
  0: 'bg-surface-alt text-text-muted', 1: 'bg-danger-soft text-danger', 2: 'bg-warning-soft text-warning', 3: 'bg-info-soft text-info', 4: 'bg-success-soft text-success',
};
const STRENGTH_BAR: Record<Strength['level'], string> = { 0: 'bg-border-strong', 1: 'bg-danger', 2: 'bg-warning', 3: 'bg-info', 4: 'bg-success' };

export const StrengthPill = ({ s, className }: { s: Strength; className?: string }) => (
  <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold', STRENGTH_TONE[s.level], className)}>{s.label}</span>
);

/** Meter + per-asset checklist + the top tips. */
export function StrengthMeter({ s, rows, title = 'Ad strength' }: { s: Strength; rows?: StrengthRow[]; title?: string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-2xs">
        <span className="font-semibold uppercase tracking-wide text-text-muted">{title}</span>
        <StrengthPill s={s} />
      </div>
      <div className="mt-1.5 grid grid-cols-4 gap-1">
        {[1, 2, 3, 4].map((i) => <span key={i} className={clsx('h-1.5 rounded-full', i <= s.level ? STRENGTH_BAR[s.level] : 'bg-surface-alt')} />)}
      </div>
      {rows && rows.length > 0 && (
        <ul className="mt-2.5 space-y-1">
          {rows.map((r) => {
            const ok = r.have >= r.target;
            const short = r.have < r.min;
            return (
              <li key={r.label} className="flex items-center gap-2 text-2xs">
                {ok ? <CheckCircle2 size={11} className="shrink-0 text-success" /> : <span className={clsx('h-1.5 w-1.5 shrink-0 rounded-full', short ? 'bg-danger' : 'bg-warning')} />}
                <span className="min-w-0 flex-1 truncate text-text-secondary">{r.label}</span>
                <span className={clsx('tabular-nums', short ? 'text-danger' : ok ? 'text-text-muted' : 'text-text-secondary')}>{r.have}/{r.target}</span>
              </li>
            );
          })}
        </ul>
      )}
      {s.tips.length > 0 && (
        <ul className="mt-2.5 space-y-1 border-t border-border pt-2">
          {s.tips.map((t) => <li key={t} className="flex gap-1.5 text-2xs leading-4 text-text-muted"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-text-muted" />{t}</li>)}
        </ul>
      )}
    </div>
  );
}

// ── preview frame ────────────────────────────────────────────────────────

/** Right-rail preview card: title, optional format switch, content, disclaimer. */
export function PreviewFrame({ title, controls, children, footer, note }: {
  title: string; controls?: React.ReactNode; children: React.ReactNode; footer?: React.ReactNode; note?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-text-muted">{title}</span>
        {controls}
      </div>
      <div className="p-4">
        {children}
        {footer && <div className="mt-4 border-t border-border pt-3">{footer}</div>}
        {note && <p className="mt-3 text-[10px] leading-relaxed text-text-muted">{note}</p>}
      </div>
    </div>
  );
}

// ── review ───────────────────────────────────────────────────────────────

/** One labelled value on the review screen. */
export const Fact = ({ label, value, sub, wide, mono }: { label: string; value: React.ReactNode; sub?: React.ReactNode; wide?: boolean; mono?: boolean }) => (
  <div className={wide ? 'sm:col-span-2' : undefined}>
    <div className="text-2xs font-semibold uppercase tracking-wide text-text-muted">{label}</div>
    <div className={clsx('mt-0.5 break-words text-[13px] font-medium text-text-primary', mono && 'font-mono text-xs')}>{value}</div>
    {sub && <div className="text-2xs text-text-muted">{sub}</div>}
  </div>
);

/** Pill list (locations, languages, topics…). */
export const ChipList = ({ items, empty, max = 14 }: { items: string[]; empty: string; max?: number }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.length === 0 && <span className="rounded-full bg-surface-alt px-2.5 py-1 text-xs text-text-secondary">{empty}</span>}
    {items.slice(0, max).map((x) => <span key={x} className="rounded-full border border-border bg-surface px-2.5 py-1 text-xs text-text-primary">{x}</span>)}
    {items.length > max && <span className="rounded-full bg-surface-alt px-2.5 py-1 text-xs text-text-muted">+{items.length - max} more</span>}
  </div>
);

/** Locations + languages block used at the bottom of every campaign review card. */
export function TargetingFacts({ locations, languages }: { locations: string[]; languages: string[] }) {
  return (
    <div className="mt-4 space-y-3 border-t border-border pt-3">
      <div>
        <div className="mb-1.5 flex items-baseline justify-between">
          <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">Locations</span>
          <span className="text-2xs text-text-muted">{locations.length ? `${locations.length} selected` : 'Everywhere'}</span>
        </div>
        <ChipList items={locations} empty="Every location Google serves" />
      </div>
      <div>
        <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-text-muted">Languages</div>
        <ChipList items={languages} empty="All languages" />
      </div>
    </div>
  );
}

/** Every blocking issue, each one jumping back to the step that fixes it. */
export function FixList<K extends string>({ groups, onJump }: { groups: Array<{ step: K; issues: string[] }>; onJump: (k: K) => void }) {
  const total = groups.reduce((n, g) => n + g.issues.length, 0);
  if (!total) return null;
  return (
    <Section title={`${total} thing${total === 1 ? '' : 's'} to fix before publishing`}>
      <ul className="space-y-1.5">
        {groups.flatMap((g) => g.issues.map((i) => (
          <li key={`${g.step}:${i}`}>
            <button type="button" onClick={() => onJump(g.step)} className="flex items-start gap-2 text-left text-[13px] text-danger hover:underline">
              <XCircle size={13} className="mt-0.5 shrink-0" /> {i}
            </button>
          </li>
        )))}
      </ul>
    </Section>
  );
}

export interface BuildRow { key: string; label: string; level: 0 | 1 | 2; state?: Pub; detail?: string }

/** Campaign → children → grandchildren, each with its live create state. */
export function BuildLog({ rows }: { rows: BuildRow[] }) {
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <div key={r.key} className={clsx('flex items-center gap-2.5 text-text-primary', r.level === 0 ? 'text-sm' : r.level === 1 ? 'pl-5 text-sm' : 'pl-10 text-[13px]')}>
          <StepIcon s={r.state} />
          <span className="min-w-0 flex-1 truncate">{r.label}</span>
          {r.detail && <span className="max-w-[45%] shrink-0 truncate text-2xs text-text-muted" title={r.detail}>{r.detail}</span>}
        </div>
      ))}
    </div>
  );
}

/** The "Publish" section: build log, the error to fix, and the save button. */
export function PublishPanel({ hint, rows, error, publishing, blocked, finished, onPublish, onCancel, label = 'Save paused campaign' }: {
  hint: string; rows: BuildRow[]; error: string | null; publishing: boolean; blocked: boolean; finished?: boolean;
  onPublish: () => void; onCancel: () => void; label?: string;
}) {
  return (
    <Section title="Publish" hint={hint}>
      <BuildLog rows={rows} />
      {error && (
        <div className="mt-4">
          <ErrorBox message={error} />
          <p className="mt-1 text-2xs text-text-muted">Fix it and publish again — anything already created is kept, not repeated.</p>
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={publishing}>Cancel</Button>
        <Button onClick={onPublish} loading={publishing} disabled={publishing || blocked || finished} iconLeft={<Check size={15} />}>
          {error ? 'Retry publish' : label}
        </Button>
      </div>
    </Section>
  );
}

/** Save row for the "add ad groups / ads / asset groups" pages. */
export function SaveBar({ issues, error, saving, onSave, onCancel, label }: {
  issues: string[]; error: string | null; saving: boolean; onSave: () => void; onCancel: () => void; label: string;
}) {
  return (
    <>
      {issues.length > 0 && !error && (
        <ul className="space-y-1 rounded-xl border border-warning/30 bg-warning-soft/40 px-4 py-3">
          {issues.slice(0, 6).map((i) => <li key={i} className="flex items-start gap-1.5 text-2xs text-warning"><CircleAlert size={11} className="mt-0.5 shrink-0" />{i}</li>)}
          {issues.length > 6 && <li className="pl-4 text-2xs text-warning">+{issues.length - 6} more</li>}
        </ul>
      )}
      {error && (
        <div>
          <ErrorBox message={error} />
          <p className="mt-1 text-2xs text-text-muted">Fix it and try again — anything already created is kept, not repeated.</p>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={onSave} loading={saving} disabled={saving || issues.length > 0} iconLeft={<Check size={15} />}>{error ? 'Retry' : label}</Button>
      </div>
    </>
  );
}
